import React, { useState, useEffect } from 'react';
import { adoptionService } from '../../services/entities/adoption.service';
import {
  AdoptionPost,
  AdoptionInterest,
  AdoptionStats,
  PaginatedAdoptionPosts,
} from '../../types/entities/adoption.types';
import { LoadingSpinner, Pagination, FilterDropdown } from '../../components';

export default function AdoptionsPage() {
  const [posts, setPosts] = useState<AdoptionPost[]>([]);
  const [interests, setInterests] = useState<AdoptionInterest[]>([]);
  const [stats, setStats] = useState<AdoptionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'posts' | 'interests' | 'reports'>('posts');

  useEffect(() => {
    fetchData();
  }, [currentPage, statusFilter, itemsPerPage, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'posts') {
        const [postsData, statsData] = await Promise.all([
          adoptionService.getAdoptionPosts(currentPage, itemsPerPage, statusFilter || undefined),
          adoptionService.getStats(),
        ]);
        setPosts(postsData.posts);
        setTotalPages(postsData.pagination.totalPages);
        setTotalItems(postsData.pagination.total);
        setStats(statsData);
      } else if (activeTab === 'interests') {
        const [interestsData, statsData] = await Promise.all([
          adoptionService.getAllInterests(currentPage, itemsPerPage),
          adoptionService.getStats(),
        ]);
        setInterests(interestsData.interests);
        setTotalPages(interestsData.pagination.totalPages);
        setTotalItems(interestsData.pagination.total);
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching adoption data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (postId: string, newStatus: 'ACTIVE' | 'ADOPTED' | 'REMOVED') => {
    try {
      await adoptionService.updatePostStatus(postId, newStatus);
      fetchData();
    } catch (error) {
      console.error('Error updating post status:', error);
      alert('Failed to update post status');
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post? This cannot be undone.')) {
      return;
    }

    try {
      await adoptionService.deletePost(postId);
      fetchData();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post');
    }
  };

  if (loading && !posts.length) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Adoption Management</h1>
        <p className="mt-2 text-gray-600">Manage adoption posts, interests, and reports</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Posts</div>
            <div className="text-2xl font-bold text-gray-900">{stats.totalPosts}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Active Posts</div>
            <div className="text-2xl font-bold text-green-600">{stats.activePosts}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Adopted</div>
            <div className="text-2xl font-bold text-blue-600">{stats.adoptedPosts}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Interests</div>
            <div className="text-2xl font-bold text-purple-600">{stats.totalInterests}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Pending Reports</div>
            <div className="text-2xl font-bold text-red-600">{stats.pendingReports}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Avg Interests</div>
            <div className="text-2xl font-bold text-orange-600">{stats.averageInterestsPerPost}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('posts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'posts'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Posts
          </button>
          <button
            onClick={() => setActiveTab('interests')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'interests'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Interests
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Reports
          </button>
        </nav>
      </div>

      {activeTab === 'posts' && (
        <>
          {/* Filters */}
          <div className="mb-6 flex gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="ADOPTED">Adopted</option>
              <option value="REMOVED">Removed</option>
            </select>
          </div>

          {/* Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
            {posts.map((post) => (
              <div key={post.id} className="bg-white rounded-lg shadow overflow-hidden">
                <img
                  src={post.imageUrl}
                  alt="Adoption post"
                  className="w-full h-48 object-cover"
                />
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {post.user.name || 'Anonymous'}
                    </span>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        post.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800'
                          : post.status === 'ADOPTED'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {post.status}
                    </span>
                  </div>
                  {post.caption && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{post.caption}</p>
                  )}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span>{post._count?.interests || 0} interests</span>
                    <span>{post._count?.reports || 0} reports</span>
                  </div>
                  <div className="text-xs text-gray-400 mb-3">
                    {new Date(post.createdAt).toLocaleDateString()}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={post.status}
                      onChange={(e) =>
                        handleStatusChange(post.id, e.target.value as any)
                      }
                      className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="ADOPTED">Adopted</option>
                      <option value="REMOVED">Removed</option>
                    </select>
                    <button
                      onClick={() => handleDeletePost(post.id)}
                      className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onPageSizeChange={setItemsPerPage}
          />
        </>
      )}

      {activeTab === 'interests' && (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Post
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Post Owner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Interested User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {interests.map((interest) => (
                  <tr key={interest.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img
                          src={interest.post.imageUrl}
                          alt="Post"
                          className="h-12 w-12 rounded object-cover"
                        />
                        <div className="ml-3">
                          <div className="text-sm text-gray-500">
                            Post ID: {interest.post.id.slice(0, 8)}...
                          </div>
                          <span
                            className={`inline-flex text-xs px-2 py-1 rounded-full ${
                              interest.post.status === 'ACTIVE'
                                ? 'bg-green-100 text-green-800'
                                : interest.post.status === 'ADOPTED'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {interest.post.status}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {interest.post.user.name || 'Anonymous'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {interest.post.user.phone || interest.post.user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {interest.user.profilePicture ? (
                          <img
                            src={interest.user.profilePicture}
                            alt={interest.user.name || 'User'}
                            className="h-8 w-8 rounded-full"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                            <span className="text-orange-600 font-medium text-sm">
                              {(interest.user.name || 'U')[0].toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {interest.user.name || 'Anonymous'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {interest.user.phone && (
                          <div className="flex items-center">
                            <svg className="h-4 w-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {interest.user.phone}
                          </div>
                        )}
                        {interest.user.email && (
                          <div className="flex items-center">
                            <svg className="h-4 w-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {interest.user.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {interest.user.city || 'N/A'}
                      </div>
                      {interest.user.location && (
                        <div className="text-xs text-gray-500">
                          {interest.user.location}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs">
                        {interest.message || <span className="text-gray-400 italic">No message</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(interest.createdAt).toLocaleDateString()}
                      <div className="text-xs text-gray-400">
                        {new Date(interest.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onPageSizeChange={setItemsPerPage}
            />
          </div>
        </>
      )}

      {activeTab === 'reports' && (
        <div className="text-center py-12">
          <p className="text-gray-600">Reports view - Coming soon</p>
        </div>
      )}
    </div>
  );
}
